import React, { ReactElement, ReactNode } from "react";
import { Flex } from "../box";
import { Button } from "../button";
import Grid, { GridProps } from "../grid/Grid";
import mediaQueries from "../utils/mediaQueries";
import useShowMoreCounter from "../utils/useShowMoreCounter";

interface INewsworthyScrollbarsContainer {
  children: ReactNode;
  stacked?: boolean;
}

export default function NewsworthyScrollbarsContainer({
  children,
  stacked = false,
  ...props
}: INewsworthyScrollbarsContainer & GridProps): ReactElement {
  const newsworthyItems = React.Children.count(children);
  const { offset, hasMore, showMore } = useShowMoreCounter(newsworthyItems, 2);

  if (stacked) {
    const items = React.Children.toArray(children);

    return (
      <div>
        <Grid {...props} gridAutoFlow="row" gridTemplateColumns="1fr" gridGap={6} maxWidth="100%" pb={3} pt={1}>
          {items.slice(0, offset)}
        </Grid>

        {hasMore && (
          <Flex justify="center" mt={4}>
            <Button
              variant="secondary"
              size="sm"
              onClick={showMore}
              rightIcon={props => (
                <svg
                  width="24"
                  height="12"
                  viewBox="0 0 24 12"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  {...props}
                >
                  <path
                    d="M2 2.5L11.7436 9.55882L22 2.5"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            >
              Show more
            </Button>
          </Flex>
        )}
      </div>
    );
  }

  return (
    <Grid
      {...props}
      gridAutoFlow="column"
      gridAutoColumns={newsworthyItems === 1 ? "100%" : ["100%", "calc(50% - 14px)"]}
      gridGap={6}
      maxWidth="100%"
      overflowX={newsworthyItems === 1 ? undefined : "scroll"}
      pb={3}
      pt={1}
      css={mediaQueries({
        "::-webkit-scrollbar": {
          height: 8,
          width: "100%",
          WebkitAppearance: "none",
        },
        "::-webkit-scrollbar-thumb": {
          borderRadius: 4,
          backgroundColor: "rgba(165, 173, 186, 0.7)",
        },
        "::-webkit-scrollbar-track": {
          borderRadius: 4,
          backgroundColor: "rgba(165, 173, 186, 0.2)",
          marginLeft: [40, 40, 182.5],
          marginRight: [40, 40, 182.5],
        },
      })}
    >
      {children}
    </Grid>
  );
}
